/**
 * Phase 9.1: Embedding Monitoring Utilities
 * Developer tools for checking embedding status and health
 */

import { supabase } from '../lib/supabase';

/**
 * Check embedding status for recent items
 * Usage: In browser console: window.checkEmbeddings()
 */
export async function checkEmbeddings(limit = 10) {
  try {
    const { data, error } = await supabase
      .schema('deepsearch').from('items')
      .select('id, title, embedding, embedding_model, created_at, updated_at')
      .not('embedding', 'is', null)
      .order('updated_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('❌ Error checking embeddings:', error);
      return null;
    }

    console.table(data.map(item => ({
      id: item.id.substring(0, 8) + '...',
      title: item.title || '(no title)',
      has_embedding: item.embedding ? '✅' : '❌',
      model: item.embedding_model || 'unknown',
      updated: new Date(item.updated_at).toLocaleString(),
    })));

    return data;
  } catch (error) {
    console.error('❌ Error:', error);
    return null;
  }
}

/**
 * Check embedding logs for monitoring
 * Usage: In browser console: window.checkEmbeddingLogs()
 */
export async function checkEmbeddingLogs(limit = 20) {
  try {
    const { data, error } = await supabase
      .schema('deepsearch').from('embedding_logs')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('❌ Error checking logs:', error);
      return null;
    }

    if (!data || data.length === 0) {
      console.log('📭 No embedding logs found');
      return [];
    }

    const summary = {
      total: data.length,
      success: data.filter(l => l.status === 'success').length,
      error: data.filter(l => l.status === 'error').length,
      pending: data.filter(l => l.status === 'pending').length,
      avgLatency: data
        .filter(l => l.latency_ms)
        .reduce((sum, l) => sum + l.latency_ms, 0) / data.filter(l => l.latency_ms).length || 0,
    };

    console.log('📊 Embedding Logs Summary:');
    console.table([summary]);
    console.log('\n📋 Recent Logs:');
    console.table(data.map(log => ({
      time: new Date(log.created_at).toLocaleString(),
      status: log.status === 'success' ? '✅' : log.status === 'error' ? '❌' : '⏳',
      latency: log.latency_ms ? `${log.latency_ms}ms` : '-',
      item_id: log.item_id.substring(0, 8) + '...',
    })));

    return { summary, logs: data };
  } catch (error) {
    console.error('❌ Error:', error);
    return null;
  }
}

/**
 * Check embedding status view (comprehensive status)
 * Usage: In browser console: window.checkEmbeddingStatus()
 */
export async function checkEmbeddingStatus(limit = 20) {
  try {
    const { data, error } = await supabase
      .schema('deepsearch').from('embedding_status')
      .select('*')
      .order('item_created_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('❌ Error checking status:', error);
      return null;
    }

    if (!data || data.length === 0) {
      console.log('📭 No items found');
      return [];
    }

    const stats = {
      total: data.length,
      with_embeddings: data.filter(i => i.has_embedding).length,
      without_embeddings: data.filter(i => !i.has_embeddings).length,
      pending: data.filter(i => i.queue_status === 'PENDING').length,
      processed: data.filter(i => i.queue_status === 'PROCESSED').length,
    };

    console.log('📊 Embedding Status Summary:');
    console.table([stats]);
    console.log('\n📋 Item Status:');
    console.table(data.map(item => ({
      title: item.title || '(no title)',
      has_embedding: item.has_embedding ? '✅' : '❌',
      queue_status: item.queue_status || '-',
      last_log: item.last_log_status || '-',
      latency: item.last_latency_ms ? `${item.last_latency_ms}ms` : '-',
    })));

    return { stats, items: data };
  } catch (error) {
    console.error('❌ Error:', error);
    return null;
  }
}

/**
 * Get items without embeddings
 * Usage: In browser console: window.getItemsWithoutEmbeddings()
 */
export async function getItemsWithoutEmbeddings() {
  try {
    const { data: session } = await supabase.auth.getSession();
    if (!session?.session?.user) {
      console.error('❌ Not authenticated');
      return null;
    }

    const { data, error } = await supabase
      .schema('deepsearch').from('items')
      .select('id, title, description, created_at')
      .eq('user_id', session.session.user.id)
      .is('embedding', null)
      .order('created_at', { ascending: false })
      .limit(50);

    if (error) {
      console.error('❌ Error:', error);
      return null;
    }

    if (!data || data.length === 0) {
      console.log('✅ All items have embeddings!');
      return [];
    }

    console.log(`📭 Found ${data.length} items without embeddings:`);
    console.table(data.map(item => ({
      id: item.id.substring(0, 8) + '...',
      title: item.title || '(no title)',
      created: new Date(item.created_at).toLocaleString(),
    })));

    return data;
  } catch (error) {
    console.error('❌ Error:', error);
    return null;
  }
}

// Expose to window for console access
if (typeof window !== 'undefined') {
  window.checkEmbeddings = checkEmbeddings;
  window.checkEmbeddingLogs = checkEmbeddingLogs;
  window.checkEmbeddingStatus = checkEmbeddingStatus;
  window.getItemsWithoutEmbeddings = getItemsWithoutEmbeddings;
  
  console.log('🔧 Embedding monitoring utilities loaded!');
  console.log('   Available commands:');
  console.log('   - checkEmbeddings() - Check recent items with embeddings');
  console.log('   - checkEmbeddingLogs() - View embedding generation logs');
  console.log('   - checkEmbeddingStatus() - Comprehensive status check');
  console.log('   - getItemsWithoutEmbeddings() - Find items needing embeddings');
}

